import React from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import {
  useGeneratedReportsMetaLazyQuery,
  useInboxItemLazyQuery,
  useInboxItemsScrollableListLazyQuery,
  useVerificationRequestLazyQuery,
  useVerificationRequestPaymentInfoQuery,
  VerificationRequestStripePaymentStatus,
} from 'src/api';

type VerificationRequestPaymentInfoSubscriptionHookParams = {
  inboxItemId: string | undefined;
  skip?: boolean;
};

export const useVerificationRequestPaymentInfoSubscription = ({
  inboxItemId,
  skip,
}: VerificationRequestPaymentInfoSubscriptionHookParams) => {
  const isDataFetched = React.useRef(false);

  const { data } = useVerificationRequestPaymentInfoQuery({
    variables: { where: { inboxItemId } },
    skip: skip || !inboxItemId,
    pollInterval: 3000,
  });

  const stripePaymentStatus = React.useMemo(() => {
    return data?.verificationRequest?.stripePaymentStatus;
  }, [data?.verificationRequest?.stripePaymentStatus]);

  const stripePaymentStatusCached = React.useRef(stripePaymentStatus);

  const [fetchInboxItem] = useInboxItemLazyQuery();
  const [fetchScrollableInboxItem] = useInboxItemsScrollableListLazyQuery();
  const [fetchVerificationRequest] = useVerificationRequestLazyQuery();
  const [fetchGeneratedReportsMeta] = useGeneratedReportsMetaLazyQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run when stripePaymentStatus changes
  React.useEffect(() => {
    if (!inboxItemId) return;

    if (stripePaymentStatus !== stripePaymentStatusCached.current) {
      stripePaymentStatusCached.current = stripePaymentStatus;

      // reset data fetched flag if payment status is processing
      if (stripePaymentStatus === VerificationRequestStripePaymentStatus.Processing) {
        isDataFetched.current = false;
      }

      if (
        stripePaymentStatus !== VerificationRequestStripePaymentStatus.Processing &&
        !isDataFetched.current
      ) {
        isDataFetched.current = true;
        (async () => {
          await Promise.all([
            fetchInboxItem({
              variables: { id: inboxItemId },
              fetchPolicy: REFETCH_POLICY,
            }),
            fetchScrollableInboxItem({
              variables: { where: { id: { equals: inboxItemId } } },
              fetchPolicy: REFETCH_POLICY,
            }),
            fetchVerificationRequest({
              variables: { filter: { inboxItemId } },
              fetchPolicy: REFETCH_POLICY,
            }),
            fetchGeneratedReportsMeta({
              variables: { inboxItemId },
              fetchPolicy: REFETCH_POLICY,
            }),
          ]);
        })();
      }
    }
  }, [stripePaymentStatus]);
};

const REFETCH_POLICY: WatchQueryFetchPolicy = 'cache-and-network';
